import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Tooltip,
  Autocomplete,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AccountBalance as LedgerIcon,
  Refresh as RefreshIcon,
  ArrowUpward as CreditIcon,
  ArrowDownward as DebitIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';
import useOrganizationSearch from '../hooks/useOrganizationSearch';
import { isPlatformLevel } from '../utils/roleUtils';
import { getBrandedTableHeadSx } from '../utils/colorUtils';
import JournalEntryLink from '../components/accounting/JournalEntryLink';
import { ProTableStats, useColumnPreferences } from '../components/pro-table';
import ProTablePreferences from '../components/pro-table/ProTablePreferences';

const TOOLBAR_SX = {
  '& .MuiInputBase-root': { minHeight: 32, height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

// Bank-account transaction types (com.thaklis.manager.enums.TransactionType).
const TRANSACTION_TYPES = [
  'TOPUP',
  'DIRECT_TOPUP',
  'INVOICE_PAYMENT',
  'REFUND',
  'TAX_PAYMENT',
  'THAKLIS_PAYMENT',
  'PAYOUT',
  'ADJUSTMENT',
  'CUSTOMER_REFUND',
  'CHEQUE_TOPUP',
  'SUBSCRIPTION_PAYMENT',
  'LEDGER',
  'TRANSFER',
];

// Explicit labels; anything else falls back to title case.
const TYPE_LABELS = {
  DIRECT_TOPUP: 'Direct Top-Up',
  TOPUP: 'Top-Up',
  CUSTOMER_REFUND: 'Refund to Customer',
  CHEQUE_TOPUP: 'Cheque Top-Up',
  SUBSCRIPTION_PAYMENT: 'Subscription',
  LEDGER: 'Payment / Entry',
  TRANSFER: 'Transfer',
};

// Table columns. Each TC keeps its own visibility/order (column settings button).
const COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'category', label: 'Category' },
  { key: 'organization', label: 'Organization' },
  { key: 'description', label: 'Description' },
  { key: 'reference', label: 'Reference', defaultVisible: false },
  { key: 'balanceBefore', label: 'Balance Before', align: 'right', defaultVisible: false },
  { key: 'balanceAfter', label: 'Balance After', align: 'right' },
  { key: 'amount', label: 'Amount', align: 'right' },
];

const EMPTY_FILTERS = {
  organizationId: '',
  transactionType: '',
  direction: '',
  status: '',
  startDate: '',
  endDate: '',
};

const TransactionLedger = ({ currentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    bank_balance: 0,
    money_in: 0,
    money_out: 0,
    pending: 0
  });
  const [loading, setLoading] = useState(false);

  // Typing-center selection — declared before the org search hook, which reads it.
  const [selectedTypingCenter, setSelectedTypingCenter] = useState(
    isPlatform ? '' : currentUser?.typingCenterId
  );
  const [typingCenters, setTypingCenters] = useState([]);

  const {
    options: internalOrgs,
    loading: internalOrgsLoading,
    onInputChange: onInternalOrgInputChange,
    filterOptions: internalOrgFilterOptions
  } = useOrganizationSearch({ typingCenterId: selectedTypingCenter });
  // Held separately: the chosen org may not be in the current search results.
  const [selectedInternalOrg, setSelectedInternalOrg] = useState(null);

  // Bank account selection — the ledger is scoped to one bank account.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');

  const columnPrefs = useColumnPreferences(`transactionLedger.${selectedTypingCenter || 'none'}`, COLUMNS);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);

  // Filters: `filters` is the editable form; `appliedFilters` is what's actually queried.
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });

  // Internal organizations for the org filter — the transaction's source org is a
  // TInternalOrganization, so the filter must match those (not mapped external orgs).
  // Backend returns them sorted by name.

  // Platform users pick a typing center; default to the first real one
  useEffect(() => {
    if (!isPlatform) return;
    ApiService.getTypingCenters()
      .then((res) => {
        const list = res.data?.data || [];
        setTypingCenters(list);
        setSelectedTypingCenter(prev => prev || list[0]?.id || '');
      })
      .catch((err) => console.error('Failed to fetch typing centers:', err));
  }, [isPlatform]);

  // Load bank accounts for the selected typing center; default to the default account.
  useEffect(() => {
    const loadBanks = async () => {
      if (!selectedTypingCenter) {
        setBankAccounts([]);
        setSelectedBankId('');
        return;
      }
      try {
        const res = await ApiService.getTcBankAccounts(selectedTypingCenter);
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setBankAccounts(list);
        const def = list.find(b => (b.is_default ?? b.isDefault)) || list[0];
        setSelectedBankId(def?.id || '');
        setPage(0);
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        setBankAccounts([]);
        setSelectedBankId('');
      }
    };
    loadBanks();
  }, [selectedTypingCenter]);

  const fetchData = useCallback(async () => {
    if (!selectedBankId) {
      setTransactions([]);
      setTotalElements(0);
      return;
    }
    setLoading(true);
    try {
      const params = { page, size: rowsPerPage };
      if (appliedFilters.transactionType) params.type = appliedFilters.transactionType;
      if (appliedFilters.direction) params.direction = appliedFilters.direction;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.organizationId) params.organizationId = appliedFilters.organizationId;
      if (appliedFilters.startDate) params.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) params.endDate = appliedFilters.endDate;

      const res = await ApiService.get(`/api/tc-bank-accounts/${selectedBankId}/transactions`, { params });
      const pageData = res.data?.data || {};
      setTransactions(Array.isArray(pageData.content) ? pageData.content : []);
      setTotalElements(pageData.total_elements ?? pageData.totalElements ?? 0);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [selectedBankId, page, rowsPerPage, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Summary tiles, scoped to the selected bank.
  const fetchSummary = useCallback(async () => {
    if (!selectedBankId) {
      setSummary({ bank_balance: 0, money_in: 0, money_out: 0, pending: 0 });
      return;
    }
    try {
      const res = await ApiService.get(`/api/tc-bank-accounts/${selectedBankId}/transactions/summary`);
      const d = res.data?.data || {};
      setSummary({
        bank_balance: Number(d.bank_balance) || 0,
        money_in: Number(d.money_in) || 0,
        money_out: Number(d.money_out) || 0,
        pending: Number(d.pending) || 0,
      });
    } catch (error) {
      console.error('Error fetching transaction summary:', error);
      setSummary({ bank_balance: 0, money_in: 0, money_out: 0, pending: 0 });
    }
  }, [selectedBankId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Filters apply as they change (no Apply button).
  const applyFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setAppliedFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };


  const handleResetFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setPage(0);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const humanize = (value) => {
    if (!value) return '';
    return value
      .toLowerCase()
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'TOPUP':
      case 'DIRECT_TOPUP':
      case 'CHEQUE_TOPUP': return 'success';
      case 'SUBSCRIPTION_PAYMENT': return 'warning';
      case 'CUSTOMER_REFUND': return 'error';
      case 'INVOICE_PAYMENT': return 'info';
      case 'THAKLIS_PAYMENT':
      case 'TAX_PAYMENT':
      case 'PAYOUT': return 'warning';
      case 'REFUND': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PENDING': return 'warning';
      case 'REJECTED':
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getEntryTypeIcon = (direction) => {
    return direction === 'CREDIT' ? (
      <CreditIcon sx={{ fontSize: 16, color: '#73BF69' }} />
    ) : (
      <DebitIcon sx={{ fontSize: 16, color: '#F2495C' }} />
    );
  };

  const dash = (
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>-</Typography>
  );

  const renderCell = (key, transaction) => {
    switch (key) {
      case 'date':
        return formatDate(transaction.created_at);
      case 'type':
        return (
          <Chip
            label={TYPE_LABELS[transaction.type] || humanize(transaction.type)}
            size="small"
            color={getTransactionTypeColor(transaction.type)}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        );
      case 'status':
        return (
          <Chip
            label={humanize(transaction.status)}
            size="small"
            color={getStatusColor(transaction.status)}
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        );
      case 'category':
        return transaction.category || dash;
      case 'organization':
        return transaction.source_organization_name ? (
          <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{transaction.source_organization_name}</Typography>
        ) : dash;
      case 'description':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography noWrap sx={{ fontSize: '0.75rem', minWidth: 0 }}>{transaction.description}</Typography>
            <JournalEntryLink id={transaction.journal_entry_id} />
          </Box>
        );
      case 'reference':
        return transaction.external_reference || dash;
      case 'balanceBefore':
        return formatCurrency(transaction.balance_before);
      case 'balanceAfter':
        return formatCurrency(transaction.balance_after);
      case 'amount':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            {getEntryTypeIcon(transaction.direction)}
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: transaction.direction === 'CREDIT' ? '#73BF69' : '#F2495C' }}>
              {transaction.direction === 'CREDIT' ? '+' : '-'}{formatCurrency(transaction.amount)}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  // Mobile card view for transactions
  const TransactionCard = ({ transaction }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Chip
            label={TYPE_LABELS[transaction.type] || humanize(transaction.type)}
            size="small"
            color={getTransactionTypeColor(transaction.type)}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getEntryTypeIcon(transaction.direction)}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                color: transaction.direction === 'CREDIT' ? '#73BF69' : '#F2495C'
              }}
            >
              {transaction.direction === 'CREDIT' ? '+' : '-'}{formatCurrency(transaction.amount)}
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
          {formatDate(transaction.created_at)}
        </Typography>
        {transaction.category && (
          <Typography sx={{ fontSize: '0.75rem', mb: 0.5 }}>{transaction.category}</Typography>
        )}
        {transaction.source_organization_name && (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
            {transaction.source_organization_name}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {transaction.description}
          </Typography>
          <JournalEntryLink id={transaction.journal_entry_id} />
        </Box>
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Chip
            label={humanize(transaction.status)}
            size="small"
            color={getStatusColor(transaction.status)}
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Summary tiles — scoped to the selected bank account. */}
      <ProTableStats
        cards={[
          { label: 'Bank Balance', value: formatCurrency(summary.bank_balance) },
          { label: 'Money In', value: formatCurrency(summary.money_in), color: 'success.main' },
          { label: 'Money Out', value: formatCurrency(summary.money_out), color: 'error.main' },
          { label: 'Pending', value: formatCurrency(summary.pending), color: 'warning.main' },
        ]}
      />

      {/* One toolbar row; filters apply as they change. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        {isPlatform && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={selectedTypingCenter} onChange={(e) => setSelectedTypingCenter(e.target.value)} displayEmpty>
              {typingCenters.map(center => (
                <MenuItem key={center.id} value={center.id} sx={{ fontSize: '0.8125rem' }}>
                  {center.name} ({center.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* Bank account selector — the ledger is scoped to one account. */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Bank Account</InputLabel>
          <Select
            value={selectedBankId}
            label="Bank Account"
            onChange={(e) => { setSelectedBankId(e.target.value); setPage(0); }}
          >
            {bankAccounts.length === 0 && (
              <MenuItem value="" disabled sx={{ fontSize: '0.8125rem' }}>No bank accounts</MenuItem>
            )}
            {bankAccounts.map(acc => {
              const accName = acc.account_name || acc.accountName || '';
              const bankName = acc.bank_name || acc.bankName || '';
              const isDefault = acc.is_default ?? acc.isDefault;
              return (
                <MenuItem key={acc.id} value={acc.id} sx={{ fontSize: '0.8125rem' }}>
                  {accName} - {bankName}{isDefault ? ' (Default)' : ''}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Autocomplete
          options={internalOrgs}
          getOptionLabel={(option) => option.name || ''}
          value={selectedInternalOrg}
          onChange={(e, newValue) => {
            setSelectedInternalOrg(newValue);
            applyFilter({ organizationId: newValue?.id || '' });
          }}
          onInputChange={onInternalOrgInputChange}
          filterOptions={internalOrgFilterOptions}
          loadingText="Searching..."
          noOptionsText="No organizations found"
          loading={internalOrgsLoading}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          sx={{ flex: '1 1 200px', minWidth: 180, '& .MuiInputBase-root': { py: '0 !important' } }}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Organization (name or TRN)..." />
          )}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filters.transactionType} label="Type" onChange={(e) => applyFilter({ transactionType: e.target.value })}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
            {TRANSACTION_TYPES.map(t => (
              <MenuItem key={t} value={t} sx={{ fontSize: '0.8125rem' }}>{TYPE_LABELS[t] || humanize(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Direction</InputLabel>
          <Select value={filters.direction} label="Direction" onChange={(e) => applyFilter({ direction: e.target.value })}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
            <MenuItem value="CREDIT" sx={{ fontSize: '0.8125rem' }}>Credit</MenuItem>
            <MenuItem value="DEBIT" sx={{ fontSize: '0.8125rem' }}>Debit</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filters.status} label="Status" onChange={(e) => applyFilter({ status: e.target.value })}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
            <MenuItem value="COMPLETED" sx={{ fontSize: '0.8125rem' }}>Completed</MenuItem>
            <MenuItem value="PENDING" sx={{ fontSize: '0.8125rem' }}>Pending</MenuItem>
            <MenuItem value="REJECTED" sx={{ fontSize: '0.8125rem' }}>Rejected</MenuItem>
            <MenuItem value="CANCELLED" sx={{ fontSize: '0.8125rem' }}>Cancelled</MenuItem>
          </Select>
        </FormControl>
        <TextField label="From" type="date" size="small" value={filters.startDate} InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }} onChange={(e) => applyFilter({ startDate: e.target.value })} />
        <TextField label="To" type="date" size="small" value={filters.endDate} InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }} onChange={(e) => applyFilter({ endDate: e.target.value })} />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Button size="small" onClick={handleResetFilters} sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}>
            Reset
          </Button>
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" disabled={loading} onClick={() => { fetchData(); fetchSummary(); }}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <ProTablePreferences columns={COLUMNS} preferences={columnPrefs} />
        </Box>
      </Box>

      {/* Transactions Table / Mobile Cards */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : !selectedBankId ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <LedgerIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Select a Bank Account
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                Choose a bank account above to view its transactions
              </Typography>
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <LedgerIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No Transactions
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                No transactions match the selected account and filters
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile Card View
            <Box sx={{ p: 2 }}>
              {transactions.map((transaction) => (
                <TransactionCard key={transaction.id} transaction={transaction} />
              ))}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer>
              <Table size="small">
                <TableHead sx={getBrandedTableHeadSx(theme, isPlatform ? 'platform' : 'tenant')}>
                  <TableRow>
                    {columnPrefs.visibleColumns.map((col) => (
                      <TableCell key={col.key} align={col.align} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        {col.key === 'amount' ? (
                          <Tooltip title="Credit (+) increases balance, Debit (-) decreases" arrow>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              Amount
                              <InfoIcon sx={{ fontSize: 12 }} />
                            </Box>
                          </Tooltip>
                        ) : col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      {columnPrefs.visibleColumns.map((col) => (
                        <TableCell key={col.key} align={col.align} sx={{ fontSize: '0.75rem', ...(col.key === 'description' && { maxWidth: 250 }) }}>
                          {renderCell(col.key, transaction)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {selectedBankId && totalElements > 0 && (
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TransactionLedger;
